import { APP_BASE_HREF } from '@angular/common';
import { provideHttpClient } from '@angular/common/http';
import { ErrorHandler, inject, NgModule, provideAppInitializer } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatIconRegistry } from '@angular/material/icon';
import { BrowserModule, DomSanitizer } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouteReuseStrategy, RouterModule } from '@angular/router';
import { CustomerSurveyClient, NotificationsClient, UserSettingsClient } from 'api-clients/agency-admin-api-client';
import { AccountClient, ApplicantMenuClient, ApplicantSidebarClient, SiteNavClient, TypesClient } from 'api-clients/applicant-api-client';
import { UserClient } from 'api-clients/identity-server-api-client';
import { AccountClient as NotificationAccountClient } from 'api-clients/notification-api-client';
import { FeatureTogglesClient } from 'api-clients/policy-api-client';
import { MgaCarrierAccessCheckClient, MgaCarrierManagementClient } from 'api-clients/rating-api-client';
import { TwilioA2PRegistrationClient } from 'api-clients/voice-and-text-api-client';
import { environment } from 'src/environments/environment';
import { initialize } from 'src/init';
import { BannerComponent } from 'src/shared/banners/banner.component';
import { BannerService } from 'src/shared/banners/banner.service';
import { SystemBannerComponent } from 'src/shared/banners/system-banner.component';
import { ConfirmationDialogComponent } from 'src/shared/confirmation-dialog/confirmation-dialog.component';
import { ErrorPagesRoutingModule } from 'src/shared/error-pages/error-pages-routing.module';
import { ErrorHandlerService } from 'src/shared/ez-error-handler.service';
import { materialDefaults, materialModules } from 'src/shared/material.module';
import { provideNetworkConnectivity } from 'src/shared/network-connectivity/network-connectivity.provider';
import { ResizableSidePanelDragHandleComponent } from 'src/shared/resizable-sidenav/resizable-side-panel-drag-handle/resizable-side-panel-drag-handle.component';
import { ResizableSidePanelDirective } from 'src/shared/resizable-sidenav/resizable-side-panel.directive';
import { UserService } from '../shared/user.service';
import { AcordEulaModalComponent } from './acord-eula/acord-eula-modal.component';
import { AppComponent } from './app.component';
import { appRoutes } from './app.routing';
import { CustomReusingStrategy } from './custom-reuse-strategy';
import { EvaSidebarComponent } from './eva-sidebar/eva-sidebar.component';
import { EvaSidebarService } from './eva-sidebar/eva-sidebar.service';
import { FiveComponentRefService } from './five-component-ref.service';
import { FooterComponent } from './footer/footer.component';
import { HeaderModule } from './header/header.module';
import { HeaderService } from './header/header.service';
import { NavMenuComponent } from './nav-menu/nav-menu.component';
import { PastDueModalComponent } from './past-due-modal/past-due-modal.component';
import { SurveyComponent } from './survey/survey.component';
import { TwilioRegistrationModalComponent } from './twilio-registration-modal/twilio-registration-modal.component';
import { WhatsNewModalComponent } from './whats-new/whats-new-modal.component';
import { EVAPaneComponent } from './workspace/eva-pane/eva-pane.component';
import { WorkspaceComponentRefService } from './workspace/workspace-component-ref.service';
import { WorkspaceModule } from './workspace/workspace.module';

@NgModule({
  declarations: [
    AppComponent,
    TwilioRegistrationModalComponent,
    SurveyComponent,
    WhatsNewModalComponent,
    PastDueModalComponent,
    AcordEulaModalComponent,
  ],
  bootstrap: [AppComponent],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    materialModules,
    RouterModule.forRoot(appRoutes, { scrollPositionRestoration: 'top', paramsInheritanceStrategy: 'always', onSameUrlNavigation: 'reload' }),
    ConfirmationDialogComponent,
    ErrorPagesRoutingModule,
    HeaderModule,
    WorkspaceModule,
    BannerComponent,
    SystemBannerComponent,
    NavMenuComponent,
    FooterComponent,
    FormsModule,
    EVAPaneComponent,
    ResizableSidePanelDirective,
    ResizableSidePanelDragHandleComponent,
    EvaSidebarComponent,
  ],
  providers: [
    { provide: UserClient, useValue: new UserClient() },
    { provide: APP_BASE_HREF, useValue: environment.fiveBaseHref },
    { provide: RouteReuseStrategy, useClass: CustomReusingStrategy },
    { provide: ErrorHandler, useClass: ErrorHandlerService },
    FiveComponentRefService,
    // This is required due to settings.service currently being provided in root. Will adjust in future MR
    { provide: TypesClient, useValue: new TypesClient() },
    { provide: UserSettingsClient, useValue: new UserSettingsClient() },
    { provide: AccountClient, useValue: new AccountClient() },
    { provide: TwilioA2PRegistrationClient, useValue: new TwilioA2PRegistrationClient() },
    { provide: CustomerSurveyClient, useValue: new CustomerSurveyClient() },
    { provide: SiteNavClient, useValue: new SiteNavClient() },
    { provide: ApplicantMenuClient, useValue: new ApplicantMenuClient() },
    { provide: ApplicantSidebarClient, useValue: new ApplicantSidebarClient() },
    { provide: NotificationsClient, useValue: new NotificationsClient() },
    provideAppInitializer(() => {
      const userService = inject(UserService);
      const workspaceComponentRef = inject(WorkspaceComponentRefService);
      const headerService = inject(HeaderService);
      const init = initialize(userService, workspaceComponentRef, headerService);
      return init();
    }),
    { provide: FeatureTogglesClient, useValue: new FeatureTogglesClient() },
    BannerService,
    materialDefaults,
    EvaSidebarService,
    provideHttpClient(),
    provideNetworkConnectivity(),
    provideAppInitializer(() => {
      const iconRegistry = inject(MatIconRegistry);
      const domSanitizer = inject(DomSanitizer);
      iconRegistry.setDefaultFontSetClass('material-symbols-outlined');
      iconRegistry.addSvgIcon(
        'warning-triangle',
        domSanitizer.bypassSecurityTrustResourceUrl(`assets/icons/warning-triangle.svg`),
      );
    }),
    { provide: MgaCarrierManagementClient, useValue: new MgaCarrierManagementClient() },
    { provide: MgaCarrierAccessCheckClient, useValue: new MgaCarrierAccessCheckClient() },
    { provide: NotificationAccountClient, useValue: new NotificationAccountClient() },
  ],
})
export class AppModule { }
